import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox"; // For "Remember me"
import { Label } from "@/components/ui/label";
import { useAuth } from "../../context/AuthContext"; // Relative path
import { Info, Loader2 } from "lucide-react"; // Import Loader2
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Link, useNavigate } from "react-router-dom";
// toast is handled by AuthContext for login errors/success

const formSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: z.string().min(1, "Password is required"), // Min 1, backend handles actual strength
  rememberMe: z.boolean().optional(),
});

type LoginFormValues = z.infer<typeof formSchema>;

const LoginForm = () => {
  const { login: loginUser, isLoading } = useAuth(); // Renamed to avoid conflict
  const navigate = useNavigate();

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
      rememberMe: false,
    },
  });

  const onSubmit = async (values: LoginFormValues) => {
    try {
      await loginUser(values.email, values.password);
      // Navigation on success can be handled by AuthContext or here
      // For now, AuthContext doesn't navigate on successful login, so we do it here.
      navigate("/"); // Navigate to homepage or dashboard after successful login
    } catch (error) {
      // Error toast is handled by AuthContext
      console.error("Login form error:", error);
      // If AuthContext re-throws, error will be caught here.
      // If specific navigation is needed based on error type (e.g. to verify email page),
      // AuthContext's login function already handles that.
    }
  };

  return (
    <div>
      <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
        <div className="flex items-start">
          <Info className="h-5 w-5 text-blue-500 mt-0.5 mr-3" />
          <div>
            <h3 className="text-sm font-medium text-blue-800">
              Welcome to PropertyLK
            </h3>
            <p className="text-sm text-blue-700 mt-1">
              Sign in to access your saved properties, receive property alerts,
              and connect with property owners.
            </p>
          </div>
        </div>
      </div>

      <form className="space-y-6" onSubmit={form.handleSubmit(onSubmit)}>
        <div>
          <Label htmlFor="email">Email address</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            {...form.register("email")}
            className="w-full mt-1"
            disabled={isLoading}
          />
          {form.formState.errors.email && (
            <p className="mt-1 text-xs text-red-600">
              {form.formState.errors.email.message}
            </p>
          )}
        </div>

        <div>
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            {...form.register("password")}
            className="w-full mt-1"
            disabled={isLoading}
          />
          {form.formState.errors.password && (
            <p className="mt-1 text-xs text-red-600">
              {form.formState.errors.password.message}
            </p>
          )}
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="rememberMe"
              {...form.register("rememberMe")}
              disabled={isLoading}
            />
            <Label
              htmlFor="rememberMe"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Remember me
            </Label>
          </div>

          <div className="text-sm">
            <Link
              to="/forgot-password"
              className="font-medium text-propertylk-blue hover:text-propertylk-darkblue"
            >
              Forgot your password?
            </Link>
          </div>
        </div>

        <div>
          <Button
            type="submit"
            className="w-full bg-propertylk-blue hover:bg-propertylk-darkblue flex items-center justify-center"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Signing in...
              </>
            ) : (
              "Sign in"
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default LoginForm;
